import { useCallback, useEffect, useRef, useState } from 'react';

import type { GuideTipActionDtoSurface, GuideTipResponse } from '@/api/generated/model';

import { useGuideTipAction } from './api';
import { undoMessage, type HidingAction } from './tip-model';

export interface PendingUndo {
  /** Changes on every hiding action, so a second undo row restarts its clock. */
  id: number;
  tip: GuideTipResponse;
  message: string;
}

export interface TipActions {
  /** Snooze, close, set aside or mark done — and offer the undo row. */
  hide: (tip: GuideTipResponse, action: HidingAction) => void;
  /** «نمایش در حال حاضر» / «نمایش دوباره» from the hub's lists. */
  restoreKey: (itemKey: string) => void;
  /** The undo row's «بازگرداندن». */
  undoLast: () => void;
  undo: PendingUndo | null;
  clearUndo: () => void;
  isPending: boolean;
}

/**
 * The hiding actions of one surface and its single undo row.
 *
 * One row per surface: a second hide replaces the first offer (the first can
 * still be brought back from the hub's «بعداً» or «کنار گذاشته‌ها»). A request
 * that fails puts the tip back (see `useGuideTipAction`) and drops the row.
 */
export function useTipActions(surface: GuideTipActionDtoSurface): TipActions {
  const action = useGuideTipAction();
  const { mutate } = action;
  const [undo, setUndo] = useState<PendingUndo | null>(null);
  const counter = useRef(0);
  // The row on screen, read by «بازگرداندن» without a state updater (updaters
  // may run twice; a request must not).
  const undoRef = useRef<PendingUndo | null>(null);
  useEffect(() => {
    undoRef.current = undo;
  }, [undo]);

  const hide = useCallback(
    (tip: GuideTipResponse, kind: HidingAction) => {
      counter.current += 1;
      const id = counter.current;
      setUndo({ id, tip, message: undoMessage(tip, kind) });
      mutate(
        { itemKey: tip.itemKey, action: kind, surface },
        {
          onError: () => setUndo((current) => (current?.id === id ? null : current)),
        },
      );
    },
    [mutate, surface],
  );

  const restoreKey = useCallback(
    (itemKey: string) => {
      mutate({ itemKey, action: 'RESTORE', surface });
    },
    [mutate, surface],
  );

  const undoLast = useCallback(() => {
    const current = undoRef.current;
    setUndo(null);
    if (current) mutate({ itemKey: current.tip.itemKey, action: 'RESTORE', surface });
  }, [mutate, surface]);

  const clearUndo = useCallback(() => setUndo(null), []);

  return { hide, restoreKey, undoLast, undo, clearUndo, isPending: action.isPending };
}
